import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/generated/openapi";
import { API_BASE_URL } from "@/config";
import {
  isTableDisplayBoard,
  POLL_MS,
  STALE_MS,
  type TableDisplayBoard,
} from "@/lib/shows/tableDisplay";
import { validatedJson } from "@/lib/ui/validatedJson";

export type TableDisplay = components["schemas"]["TableDisplayOut"];
export type TableDisplayIn = components["schemas"]["TableDisplayIn"];

/** The board's connection to the API: `reconnecting` hides prices, `gone` is a code that stopped
 *  working (rotated, or its show was removed). */
export type BoardStatus = "loading" | "live" | "reconnecting" | "gone";

class CodeGone extends Error {}

/**
 * Polls the public board for one display code. Anonymous: it sends no auth header, and the route is
 * `include_in_schema=False`, so the wire shape is hand-written in `lib/shows/tableDisplay`.
 *
 * A failed or overdue poll reads `reconnecting` at once and the last board is kept but never
 * shown as live, so a price drawn before the connection dropped cannot pass for a current one.
 */
export function usePublicTableDisplay(code: string | null) {
  const query = useQuery({
    queryKey: ["table-display", code],
    enabled: !!code,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
    queryFn: async (): Promise<TableDisplayBoard> => {
      // A hung request would otherwise hold the last good board on screen as if it were live.
      const resp = await fetch(`${API_BASE_URL}/table-display/${encodeURIComponent(code!)}`, {
        signal: AbortSignal.timeout(STALE_MS),
        cache: "no-store",
      });
      if (resp.status === 404) throw new CodeGone();
      if (!resp.ok) throw new Error("table display unavailable");
      return await validatedJson(resp, isTableDisplayBoard, "Table display response was invalid");
    },
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  const overdue = query.dataUpdatedAt > 0 && now - query.dataUpdatedAt > STALE_MS;
  const status: BoardStatus = query.error instanceof CodeGone
    ? "gone"
    : query.isError || overdue
    ? "reconnecting"
    : query.data
    ? "live"
    : "loading";
  return { board: query.data ?? null, status };
}

/** The vendor's Table display state for one operating show, refreshed while its card is on screen
 *  so Connected follows the board's polls. */
export function useTableDisplay(eventPublicId: string | null, subscribed = true) {
  return useQuery({
    queryKey: ["events", "display", eventPublicId],
    enabled: !!eventPublicId,
    subscribed,
    refetchInterval: 5_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/events/{public_id}/display", {
        params: { path: { public_id: eventPublicId! } },
      });
      if (error) throw new Error("failed to load the table display");
      return data;
    },
  });
}

/** Mints a display code for the operating show, or rotates it, which ends the old one. */
export function useMintTableDisplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (publicId: string) => {
      const { data, error } = await api.POST("/api/v1/events/{public_id}/display", {
        params: { path: { public_id: publicId } },
      });
      if (error) throw new Error("failed to set up the table display");
      return data;
    },
    onSuccess: (data, publicId) => qc.setQueryData(["events", "display", publicId], data),
  });
}

/** Pauses or resumes the board and sets its display-only price hide. */
export function useUpdateTableDisplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { publicId: string; body: TableDisplayIn; }) => {
      const { data, error } = await api.PATCH("/api/v1/events/{public_id}/display", {
        params: { path: { public_id: input.publicId } },
        body: input.body,
      });
      if (error) throw new Error("failed to update the table display");
      return data;
    },
    onSuccess: (data, input) => qc.setQueryData(["events", "display", input.publicId], data),
  });
}
